import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Box,
    Typography,
    IconButton,
    Card,
    CardActionArea,
    CardContent,
    Fade,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    Close as CloseIcon,
    Person as PersonIcon,
    LocationOn as LocationIcon,
    Edit as EditIcon,
} from "@mui/icons-material";

interface EditChoiceDialogProps {
    open: boolean;
    onClose: () => void;
    onEditUser: () => void;
    onEditLocation: () => void;
}

const EditChoiceDialog: React.FC<EditChoiceDialogProps> = ({
    open,
    onClose,
    onEditUser,
    onEditLocation,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Fade}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    fontWeight: 700,
                    fontSize: "1.6rem",
                    textAlign: "center",
                    pb: 1,
                    pt: 3,
                    position: "relative",
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <EditIcon color="primary" sx={{ fontSize: 30, textShadow: "0 2px 6px rgba(0,0,0,0.15)" }} />
                    Edit User Information
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 12,
                        top: 12,
                        color: "grey.500",
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 4, pt: 2 }}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    mb={4}
                >
                    Select which information you'd like to modify
                </Typography>

                {/* Option Cards */}
                <Box
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    gap={2}
                    justifyContent="center"
                >
                    {/* Edit User Details */}
                    <Fade in={open} timeout={400}>
                        <Card
                            elevation={0}
                            sx={{
                                flex: 1,
                                border: "2px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    borderColor: "primary.main",
                                    boxShadow: "0 8px 24px rgba(25,118,210,0.2)",
                                    transform: "translateY(-3px)",
                                    background: "linear-gradient(135deg, #e3f2fd, #ffffff)",
                                },
                            }}
                        >
                            <CardActionArea
                                onClick={() => {
                                    onEditUser();
                                    onClose();
                                }}
                                sx={{ p: 2.5, borderRadius: 2 }}
                            >
                                <CardContent sx={{ p: 0 }}>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "white",
                                                background: "linear-gradient(135deg, #42a5f5, #1e88e5)",
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                            }}
                                        >
                                            <PersonIcon sx={{ fontSize: 32 }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                                User Details
                                            </Typography>
                                            
                                        </Box>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Fade>

                    {/* Edit Location */}
                    <Fade in={open} timeout={600}>
                        <Card
                            elevation={0}
                            sx={{
                                flex: 1,
                                border: "2px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    borderColor: "secondary.main",
                                    boxShadow: "0 8px 24px rgba(156,39,176,0.2)",
                                    transform: "translateY(-3px)",
                                    background: "linear-gradient(135deg, #f3e5f5, #ffffff)",
                                },
                            }}
                        >
                            <CardActionArea
                                onClick={() => {
                                    onEditLocation();
                                    onClose();
                                }}
                                sx={{ p: 2.5, borderRadius: 2 }}
                            >
                                <CardContent sx={{ p: 0 }}>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "white",
                                                background: "linear-gradient(135deg, #ab47bc, #8e24aa)",
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                            }}
                                        >
                                            <LocationIcon sx={{ fontSize: 32 }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                                Location
                                            </Typography>
                                            
                                        </Box>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Fade>
                </Box>

                {/* Cancel Button */}
                <Box display="flex" justifyContent="center" mt={4}>
                    <Button
                        onClick={onClose}
                        size="large"
                        sx={{
                            minWidth: 120,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { bgcolor: "grey.100" },
                        }}
                    >
                        Cancel
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default EditChoiceDialog;
